import { useCallback, useRef, useState } from "react";
import { translateStream } from "@/lib/api";

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

export function useTranslator() {
  const [segments, setSegments] = useState([]);
  const pendingRef = useRef({});
  const lastIdRef = useRef({});
  const genRef = useRef({});

  const patch = useCallback((id, fn) => {
    setSegments((s) => s.map((x) => (x.id === id ? { ...x, ...(typeof fn === "function" ? fn(x) : fn) } : x)));
  }, []);

  const upsert = useCallback((seg) => {
    setSegments((s) => (s.some((x) => x.id === seg.id) ? s.map((x) => (x.id === seg.id ? { ...x, ...seg } : x)) : [...s, seg]));
  }, []);

  const setPending = useCallback(
    (source, text) => {
      const id = pendingRef.current[source];
      if (!text) {
        if (id) {
          pendingRef.current[source] = null;
          setSegments((s) => s.filter((x) => !(x.id === id && x.status === "listening")));
        }
        return;
      }
      const newId = id || uid();
      pendingRef.current[source] = newId;
      upsert({ id: newId, source, original: text, translation: "", status: "listening", ts: Date.now() });
    },
    [upsert]
  );

  const addUtterance = useCallback(
    (source, original, { onPartial, onDone } = {}, replace = false) => {
      const prevId = replace ? lastIdRef.current[source] : null;
      const pendingId = pendingRef.current[source];
      const id = prevId || pendingId || uid();
      if (prevId && pendingId && pendingId !== prevId) setSegments((s) => s.filter((x) => x.id !== pendingId));
      pendingRef.current[source] = null;
      lastIdRef.current[source] = id;
      const gen = (genRef.current[id] || 0) + 1;
      genRef.current[id] = gen;
      const fresh = () => genRef.current[id] === gen;
      const seg = { id, source, original, translation: "", status: "translating", ts: Date.now() };
      upsert(seg);
      let acc = "";
      translateStream(original, source, {
        onDelta: (d) => {
          if (!fresh()) return;
          acc += d;
          patch(id, { translation: acc });
          onPartial?.({ ...seg, translation: acc });
        },
        onDone: (t) => {
          if (!fresh()) return;
          patch(id, { translation: t, status: "done" });
          onDone?.({ ...seg, translation: t, status: "done" });
        },
        onError: () => fresh() && patch(id, { status: "error" }),
      });
      return id;
    },
    [patch, upsert]
  );

  const addPhrase = useCallback((source, original, translation) => {
    const seg = { id: uid(), source, original, translation, status: "done", ts: Date.now() };
    setSegments((s) => [...s, seg]);
    return seg;
  }, []);

  const clear = useCallback(() => {
    pendingRef.current = {};
    lastIdRef.current = {};
    setSegments([]);
  }, []);

  return { segments, addUtterance, addPhrase, setPending, upsert, clear, setSegments };
}
